import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from 'wasp/client/operations';
import { getFacilityById, updateFacility } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import { 
  ArrowLeft,
  MapPin, 
  Building2, 
  Wifi, 
  Car,
  FileText,
  Tag,
  Image,
  Upload,
  Save,
  Eye,
  AlertCircle,
  Coffee,
  Monitor,
  Users,
  Clock,
  Calendar,
  Settings
} from 'lucide-react';
import { DashboardLayout } from '../../../client/components/Sidebar';
import { useLocationContext } from '../../../client/contexts/LocationContext';
import { resolveLocationSlug, locationSectionPath } from '../../../client/lib/locationPaths';
import { 
  IndividualEditPageLayout, 
  SidebarSection, 
  ActionButtons,
  ActionButton,
  ActionButtonSecondary
} from '../../../components/layout';

interface FacilityFormData {
  title: string;
  description: string;
  contentBody: string;
  category: string;
  facilityType: string;
  tags: string;
  images: string[];
  visibility: 'PUBLIC' | 'VERIFIED' | 'SUBSCRIBER';
  isPinned: boolean;
  requiresApproval: boolean;
  expiryDate: string;
  isAvailable: boolean;
  reservationRequired: boolean;
  operatingHours: string;
  location: string;
  contact: string;
}

const FACILITY_CATEGORIES = [
  { value: 'vending', label: 'Vending Machines', icon: Coffee },
  { value: 'equipment', label: 'Equipment', icon: Monitor },
  { value: 'rooms', label: 'Rooms', icon: Building2 },
  { value: 'coworking', label: 'Co-Working Spaces', icon: Users },
  { value: 'general', label: 'General', icon: MapPin }
];

const FACILITY_TYPES = [
  { value: 'available', label: 'Available', description: 'Freely available for use' },
  { value: 'reservable', label: 'Reservable', description: 'Requires advance reservation' },
  { value: 'walkin', label: 'Walk-in Only', description: 'First come, first served' }
];

const VISIBILITY_OPTIONS = [
  { value: 'PUBLIC', label: 'Public', description: 'Visible to everyone' },
  { value: 'VERIFIED', label: 'Verified Members', description: 'Visible to verified members only' },
  { value: 'SUBSCRIBER', label: 'Subscribers', description: 'Visible to subscribers' }
];

export default function EditFacilityPage() {
  const { facilityId, slug } = useParams<{ facilityId: string; slug?: string }>();
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const locSlug = resolveLocationSlug(routerLocation.pathname, slug, selectedLocation);
  const facilityDetailPath = locationSectionPath(locSlug, 'facilities', facilityId!);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const [formData, setFormData] = useState<FacilityFormData>({
    title: '',
    description: '',
    contentBody: '',
    category: 'general',
    facilityType: 'available',
    tags: '',
    images: [],
    visibility: 'VERIFIED',
    isPinned: false,
    requiresApproval: false,
    expiryDate: '',
    isAvailable: true,
    reservationRequired: false,
    operatingHours: '',
    location: '',
    contact: ''
  });

  // Fetch facility data
  const { 
    data: facility, 
    isLoading: isFacilityLoading, 
    error: facilityError 
  } = useQuery(getFacilityById, { facilityId: facilityId! });

  // Populate form data when facility is loaded
  useEffect(() => {
    if (facility) {
      setFormData({
        title: facility.title || '',
        description: facility.description || '',
        contentBody: facility.contentBody || '',
        category: facility.category || 'general',
        facilityType: facility.metadata?.facilityType || 'available',
        tags: facility.tags?.join(', ') || '',
        images: facility.images || [],
        visibility: facility.visibility as any || 'VERIFIED',
        isPinned: facility.isPinned || false,
        requiresApproval: facility.requiresApproval || false,
        expiryDate: facility.expiryDate ? new Date(facility.expiryDate).toISOString().split('T')[0] : '',
        isAvailable: facility.metadata?.isAvailable !== false,
        reservationRequired: facility.metadata?.reservationRequired || false,
        operatingHours: facility.metadata?.operatingHours || '',
        location: facility.metadata?.facilityLocation || '',
        contact: facility.metadata?.contact || ''
      });
    }
  }, [facility]);

  const handleChange = (field: keyof FacilityFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const updateData = {
        facilityId: facilityId!,
        title: formData.title.trim(),
        description: formData.description.trim(),
        contentBody: formData.contentBody.trim(),
        visibility: formData.visibility,
        category: formData.category,
        facilityType: formData.facilityType,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(Boolean),
        images: formData.images,
        isPinned: formData.isPinned,
        metadata: {
          facilityType: formData.facilityType,
          isAvailable: formData.isAvailable,
          reservationRequired: formData.reservationRequired,
          operatingHours: formData.operatingHours,
          facilityLocation: formData.location,
          contact: formData.contact
        }
      };

      await updateFacility(updateData);
      
      // Navigate back to facility detail page
      navigate(facilityDetailPath);
    } catch (error) {
      console.error('Error updating facility:', error);
      setErrors({ submit: 'Failed to update facility. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(facilityDetailPath);
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h2>
            <p className="text-gray-600">Please log in to edit this facility.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (isFacilityLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading facility...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (facilityError || !facility) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <MapPin className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Facility Not Found</h2>
            <p className="text-gray-600">The facility you're looking for doesn't exist or you don't have permission to view it.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const sidebarContent = (
    <>
      <SidebarSection isFirst>
        <ActionButtons>
          <ActionButton
            type="submit"
            form="facility-form"
            disabled={isSubmitting}
            icon={Save}
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Updating...
              </>
            ) : (
              'Update'
            )}
          </ActionButton>
          
          <ActionButtonSecondary
            onClick={() => navigate(facilityDetailPath)}
            disabled={isSubmitting}
          >
            Cancel
          </ActionButtonSecondary>
        </ActionButtons>
      </SidebarSection>

      <SidebarSection title="Settings">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Visibility
            </label>
            <select
              value={formData.visibility}
              onChange={(e) => handleChange('visibility', e.target.value as any)}
              className="w-full px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent"
            >
              {VISIBILITY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isAvailable}
                onChange={(e) => handleChange('isAvailable', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Currently Available</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.reservationRequired}
                onChange={(e) => handleChange('reservationRequired', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Reservation Required</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isPinned}
                onChange={(e) => handleChange('isPinned', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Pin to Top</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.requiresApproval}
                onChange={(e) => handleChange('requiresApproval', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Requires Approval</span>
            </label>
          </div>
        </div>
      </SidebarSection>

      <SidebarSection title="Location Details">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Specific Location
            </label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => handleChange('location', e.target.value)}
              className="w-full px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent"
              placeholder="e.g., Level 2 Main Atrium, Engineering Lab"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Expiry Date (Optional)
            </label>
            <input
              type="date"
              value={formData.expiryDate}
              onChange={(e) => handleChange('expiryDate', e.target.value)}
              className="w-full px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent"
            />
            <p className="text-sm text-gray-500 mt-1">When should this facility listing expire?</p>
          </div>
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualEditPageLayout sidebarContent={sidebarContent}>
                    {/* Header */}
                    <div className="mb-6">
                      <div className="flex items-center gap-3 mb-4">
                        <MapPin className="w-8 h-8 text-teal-600" />
                        <div>
                          <h1 className="text-2xl font-bold text-gray-900">Edit Facility</h1>
                          <p className="text-gray-600">Location: {facility.location?.name || selectedLocation?.name || 'No location selected'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Form */}
                    <form id="facility-form" onSubmit={handleSubmit}>
              {/* What are you posting? */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">What facility are you editing?</h3>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                    errors.title ? 'border-destructive bg-destructive/10' : 'border-input'
                  }`}
                  placeholder="e.g., Snack Vending Machine - Level 2, 3D Printer - Engineering Lab, Conference Room A - Level 3"
                  required
                />
                {errors.title && (
                  <p className="text-destructive text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.title}
                  </p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Short Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  rows={3}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                    errors.description ? 'border-destructive bg-destructive/10' : 'border-input'
                  }`}
                  placeholder="Brief description of the facility, its purpose, and key features..."
                  required
                />
                {errors.description && (
                  <p className="text-destructive text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.description}
                  </p>
                )}
              </div>

              {/* Additional row for Left/Middle columns */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left sub-column */}
                <div className="space-y-4">
                  {/* Category */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category *
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => handleChange('category', e.target.value)}
                      className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                    >
                      {FACILITY_CATEGORIES.map((cat) => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Facility Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Access Type *
                    </label>
                    <select
                      value={formData.facilityType}
                      onChange={(e) => handleChange('facilityType', e.target.value)}
                      className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                    >
                      {FACILITY_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Right sub-column */}
                <div className="space-y-4">
                  {/* Operating Hours */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Operating Hours
                    </label>
                    <input
                      type="text"
                      value={formData.operatingHours}
                      onChange={(e) => handleChange('operatingHours', e.target.value)}
                      className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                      placeholder="e.g., Mon-Fri: 6:00 AM - 10:00 PM, 24/7"
                    />
                  </div>

                  {/* Contact Information */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contact Information
                    </label>
                    <input
                      type="text"
                      value={formData.contact}
                      onChange={(e) => handleChange('contact', e.target.value)}
                      className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                      placeholder="Phone, email, or person to contact"
                    />
                  </div>
                </div>
              </div>

              {/* Detailed Content */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Detailed Information
                </label>
                <textarea
                  value={formData.contentBody}
                  onChange={(e) => handleChange('contentBody', e.target.value)}
                  rows={8}
                  className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                  placeholder="Detailed description, usage instructions, features, pricing, restrictions, safety information..."
                />
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tags
                </label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => handleChange('tags', e.target.value)}
                  className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                  placeholder="e.g., printing, snacks, meeting room, wifi (separate with commas)"
                />
                <p className="text-sm text-gray-500 mt-2">Separate tags with commas</p>
              </div>

              {/* Error message */}
              {errors.submit && (
                <div className="mt-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {errors.submit}
                </div>
              )}
                    </form>
    </IndividualEditPageLayout>
  );
}


